/**
 * 路由配置模块
 * 
 * 定义前端路由结构和路由守卫，实现权限控制和页面重定向逻辑。
 * 使用 Vue Router 的 history 模式，支持懒加载组件。
 */
import Vue from "vue";
import VueRouter from "vue-router";

// 引入状态管理，用于路由守卫中的权限判断
import store from "@/store";

// 安装 Vue Router 插件
Vue.use(VueRouter);

/**
 * 路由配置数组
 * 
 * 每个路由对象包含：
 * - path: 路径
 * - name: 路由名称，用于编程式导航
 * - meta: 元信息，可包含 requiresAuth 等自定义属性
 * - component: 组件（使用懒加载优化首屏加载）
 */
const routes = [
  /**
   * 默认路由：重定向到用户登录页面
   * 
   * 用户访问根路径时自动跳转到用户登录页。
   */
  {
    path: "/",
    redirect: "/auth/user"
  },
  /**
   * 认证页面路由
   * 
   * 根据 URL 参数 :role 动态切换登录/注册模式（user/collector/admin）。
   * 
   * @param {string} role - 角色类型：user（用户）、collector（采集者）、admin（管理员）
   */
  {
    path: "/auth/:role",
    name: "Auth",
    // 懒加载认证组件，减少首屏体积
    component: () => import("@/views/auth/AuthView.vue")
  },
  /**
   * 控制台页面路由
   * 
   * 用户和管理员的主控制台，包含积分管理、项目管理、用户管理等功能。
   * 需要登录认证才能访问。
   */
  {
    path: "/dashboard",
    name: "Dashboard",
    meta: { requiresAuth: true },  // 标记需要认证
    component: () => import("@/views/dashboard/DashboardView.vue")
  },
  /**
   * 任务管理页面路由
   * 
   * 用户创建和管理采集任务的页面，支持单条和批量任务创建。
   * 需要登录认证才能访问。
   */
  {
    path: "/task",
    name: "Task",
    meta: { requiresAuth: true },
    component: () => import("@/views/task/TaskView.vue")
  },
  /**
   * 采集者工作台路由
   * 
   * 采集者领取任务、提交结果和查看统计的专用页面。
   * 需要登录认证才能访问。
   */
  {
    path: "/collector",
    name: "Collector",
    meta: { requiresAuth: true },
    component: () => import("@/views/collector/CollectorView.vue")
  },
  /**
   * API 文档页面路由
   * 
   * 展示用户端和采集者端的 API 接口文档，便于开发者集成。
   * 需要登录认证才能访问。
   */
  {
    path: "/api-doc",
    name: "ApiDoc",
    meta: { requiresAuth: true },
    component: () => import("@/views/api/ApiDocView.vue")
  },
  /**
   * 404 路由：重定向到首页
   * 
   * 处理所有未匹配的路由，统一重定向到根路径。
   */
  {
    path: "*",
    redirect: "/"
  }
];

/**
 * 创建 Vue Router 实例
 * 
 * @param {Object} options - 路由配置选项
 * @param {string} mode - 路由模式：history（无 # 符号）
 * @param {Array} routes - 路由配置数组
 */
const router = new VueRouter({
  // 使用 HTML5 History 模式，URL 中不含 # 符号
  mode: "history",
  // 应用路由配置
  routes
});

/**
 * 全局前置路由守卫
 * 
 * 在每次路由跳转前执行，负责：
 * 1. 初始化会话状态（首次加载时）
 * 2. 权限验证（未登录用户无法访问需要认证的页面）
 * 3. 角色验证（采集者不能访问用户控制台，反之亦然）
 * 4. 已登录用户访问登录页时自动重定向到对应控制台
 * 
 * @param {Route} to - 目标路由对象
 * @param {Route} from - 当前路由对象
 * @param {Function} next - 导航控制函数，调用 next() 继续导航，next(path) 重定向
 */
router.beforeEach(async (to, from, next) => {
  // 1. 首次加载时初始化会话状态
  if (!store.state.loaded) {
    try {
      // 获取当前登录用户信息，初始化会话
      await store.dispatch("loadSession");
    } catch (error) {
      // 获取失败则标记为未认证状态
      store.commit("SET_SESSION", { authenticated: false });
    }
  }

  // 2. 权限验证：未登录用户访问需要认证的页面时重定向到登录页
  if (to.meta.requiresAuth && !store.state.authenticated) {
    next("/auth/user");
    return;
  }

  // 3. 已登录用户访问登录页时重定向到对应控制台
  if (to.name === "Auth" && store.state.authenticated) {
    // 采集者重定向到采集者工作台
    if (store.state.role === "collector") {
      next("/collector");
    } else {
      // 用户和管理员重定向到控制台
      next("/dashboard");
    }
    return;
  }

  // 4. 角色验证：采集者不能访问用户控制台（管理员除外）
  if (to.name === "Dashboard" && store.state.role === "collector") {
    next("/collector");
    return;
  }

  // 5. 角色验证：非采集者不能访问采集者工作台（管理员除外）
  if (to.name === "Collector" && store.state.role !== "collector" && store.state.role !== "admin") {
    next("/dashboard");
    return;
  }

  // 6. 管理员模拟登录处理
  if (store.state.role === "admin") {
    const impersonateUser = localStorage.getItem('admin_impersonate_user');
    const impersonateCollector = localStorage.getItem('admin_impersonate_collector');
    
    if (impersonateUser && to.name === "Dashboard") {
      try {
        const userInfo = JSON.parse(impersonateUser);
        store.commit('SET_IMPERSONATION', { type: 'user', account: userInfo });
      } catch (e) {
        localStorage.removeItem('admin_impersonate_user');
      }
    } else if (impersonateCollector && to.name === "Collector") {
      try {
        const collectorInfo = JSON.parse(impersonateCollector);
        store.commit('SET_IMPERSONATION', { type: 'collector', account: collectorInfo });
      } catch (e) {
        localStorage.removeItem('admin_impersonate_collector');
      }
    } else {
      store.commit('SET_IMPERSONATION', null);
    }
  }

  // 6. 所有验证通过，继续导航
  next();
});

/**
 * 导出路由实例供 Vue 应用使用
 */
export default router;
